import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";

const AboutVisionMissionSection: React.FC = () => {
  const { t, dir } = useLanguage();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const isRtl = dir === "rtl";

  const content = (t as any)?.aboutVisionMission || {};
  const image = content.image || "/images/support-intro-graduate.jpg";
  const visionTitle = content.visionTitle || "VISION";
  const visionParagraphs = (content.visionParagraphs || []) as string[];
  const missionTitle = content.missionTitle || "MISSION";
  const missionParagraphs = (content.missionParagraphs || []) as string[];

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.18 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-white py-16 sm:py-20 lg:py-24"
      aria-labelledby="about-vision-title"
    >
      <div className="site-container flex flex-col gap-10 overflow-hidden">
        <div
          className={`transition-all duration-700 ease-out ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <img
            src={image}
            alt=""
            className="h-52 w-full object-cover sm:h-64 lg:h-[350px]"
          />
        </div>

        <div
          className={`grid gap-8 transition-all duration-700 ease-out lg:grid-cols-2 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
          style={{ transitionDelay: "120ms" }}
        >
          <div
            className={`flex flex-col gap-3.5 ${
              isRtl ? "items-end text-right" : "items-start text-left"
            }`}
          >
            <h2
              id="about-vision-title"
              className="font-sans text-2xl font-semibold uppercase leading-tight text-teal-900 sm:text-[24px] sm:leading-[1.2]"
            >
              {visionTitle}
            </h2>
            <div className="flex flex-col gap-3 font-sans text-sm font-normal leading-6 text-black sm:text-base">
              {visionParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>

          <div
            className={`flex flex-col gap-3 ${
              isRtl ? "items-end text-right" : "items-start text-left"
            }`}
          >
            <h2 className="font-sans text-2xl font-semibold uppercase leading-tight text-teal-900 sm:text-[24px] sm:leading-[1.2]">
              {missionTitle}
            </h2>
            <div className="flex flex-col gap-3 font-sans text-sm font-normal leading-6 text-black sm:text-base">
              {missionParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutVisionMissionSection;
